import { AfterViewChecked, Directive, Input } from '@angular/core';
import { TreeTable } from 'primeng/treetable';
import { FilterMetadata } from 'primeng/api';

@Directive({
	selector: 'p-treeTable[appCustomTreeTableOverrideFilters]',
	standalone: true
})
export class CustomTreeTableOverrideFiltersDirective implements AfterViewChecked {

	@Input() appCustomTreeTableOverrideFilters: {
		resetToFirstPage: boolean,
		scrollToTop: boolean
	} = { resetToFirstPage: true, scrollToTop: true };

	constructor(private treeTable: TreeTable) {
	}

	ngAfterViewChecked() {
		/**
		 * Surcharge de la méthode _filter qui remet systématiquement à la page 1 et qui réinitialise le scroll
		 */
		this.treeTable._filter = () => {
			// Vérifie si l'on doit empêcher le retour à la première page en cas de filtre
			if (this.treeTable.lazy) {
				this.treeTable.onLazyLoad.emit(this.treeTable.createLazyLoadMetadata());
			}
			else {
				if (!this.treeTable.value) {
					return;
				}

				if (!this.treeTable.hasFilter()) {
					this.treeTable.filteredNodes = null;
					if (this.treeTable.paginator) {
						this.treeTable.totalRecords = this.treeTable.value ? this.treeTable.value.length : 0;
					}
				}
				else {
					let globalFilterFieldsArray;
					if (this.treeTable.filters['global']) {
						if (!this.treeTable.columns && !this.treeTable.globalFilterFields) {
							throw new Error('Global filtering requires dynamic columns or globalFilterFields to be defined.');
						}
						else {
							globalFilterFieldsArray = this.treeTable.globalFilterFields || this.treeTable.columns;
						}
					}

					this.treeTable.filteredNodes = [];
					const isStrictMode = this.treeTable.filterMode === 'strict';
					let isValueChanged = false;

					for (const node of this.treeTable.value) {
						let copyNode = { ...node };
						let localMatch = true;
						let globalMatch = false;
						let paramsWithoutNode;

						for (const prop in this.treeTable.filters) {
							if (this.treeTable.filters.hasOwnProperty(prop) && prop !== 'global') {
								const filterMeta = <FilterMetadata>this.treeTable.filters[prop];
								const filterField = prop;
								const filterValue = filterMeta.value;
								const filterMatchMode = filterMeta.matchMode || 'startsWith';
								const filterConstraint = (<any>this.treeTable.filterService).filters[filterMatchMode];
								paramsWithoutNode = { filterField, filterValue, filterConstraint, isStrictMode };
								if ((isStrictMode && !(this.treeTable.findFilteredNodes(copyNode, paramsWithoutNode) || this.treeTable.isFilterMatched(copyNode, paramsWithoutNode))) ||
									(!isStrictMode && !(this.treeTable.isFilterMatched(copyNode, paramsWithoutNode) || this.treeTable.findFilteredNodes(copyNode, paramsWithoutNode)))) {
									localMatch = false;
								}

								if (!localMatch) {
									break;
								}
							}
						}

						if (this.treeTable.filters['global'] && !globalMatch && globalFilterFieldsArray) {
							const copyNodeForGlobal = { ...copyNode };
							const filterField = undefined;
							const filterValue = this.treeTable.filters['global'].value;
							const filterConstraint = (<any>this.treeTable.filterService).filters[(<any>this.treeTable.filters)['global'].matchMode];
							paramsWithoutNode = { filterField, filterValue, filterConstraint, isStrictMode, globalFilterFieldsArray };

							if (
								(isStrictMode && (this.treeTable.findFilteredNodes(copyNodeForGlobal, paramsWithoutNode) || this.treeTable.isFilterMatched(copyNodeForGlobal, paramsWithoutNode))) ||
								(!isStrictMode && (this.treeTable.isFilterMatched(copyNodeForGlobal, paramsWithoutNode) || this.treeTable.findFilteredNodes(copyNodeForGlobal, paramsWithoutNode)))
							) {
								globalMatch = true;
								copyNode = copyNodeForGlobal;
							}
						}

						let matches = localMatch;
						if (this.treeTable.filters['global']) {
							matches = localMatch && globalMatch;
						}

						if (matches) {
							this.treeTable.filteredNodes.push(copyNode);
						}

						isValueChanged = isValueChanged || !localMatch || globalMatch || (localMatch && this.treeTable.filteredNodes.length > 0) || (!globalMatch && this.treeTable.filteredNodes.length === 0);
					}

					if (!isValueChanged) {
						this.treeTable.filteredNodes = null;
					}

					if (this.treeTable.paginator) {
						this.treeTable.totalRecords = this.treeTable.filteredNodes ? this.treeTable.filteredNodes.length : this.treeTable.value ? this.treeTable.value.length : 0;
					}
				}
				this.treeTable.cd.markForCheck();
			}

			if (this.appCustomTreeTableOverrideFilters.resetToFirstPage) {
				this.treeTable.first = 0;
			}

			const filteredValue = this.treeTable.filteredNodes || this.treeTable.value;

			this.treeTable.onFilter.emit({
				filters: this.treeTable.filters,
				filteredValue: filteredValue
			});

			this.treeTable.tableService.onUIUpdate(filteredValue);
			this.treeTable.updateSerializedValue();

			if (this.treeTable.scrollable && this.appCustomTreeTableOverrideFilters.scrollToTop) {
				this.treeTable.resetScrollTop();
			}
		};
	}
}
